import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { StatusChip } from "@/components/StatusChip";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ShareResults } from "@/components/ShareResults";
import { API } from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import OverviewTab from "@/pages/tabs/OverviewTab";
import PositionsTab from "@/pages/tabs/PositionsTab";
import VotersTab from "@/pages/tabs/VotersTab";
import NominationsTab from "@/pages/tabs/NominationsTab";
import ResultsTab from "@/pages/tabs/ResultsTab";
import AuditTab from "@/pages/tabs/AuditTab";
import { RocketLaunch, LockSimple, Copy, SpinnerGap, Trophy, Ticket, Broadcast } from "@phosphor-icons/react";

function BuyElectionDialog({ electionId, paidTier, onDone }) {
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { if (open) api.get("/billing").then(({ data }) => setBilling(data)); }, [open]);

  const buy = async (plan) => {
    setBusy(plan);
    try {
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/billing/checkout", { plan, cadence: "per_election", election_id: electionId, callback_url, country: billing?.country });
      if (data.mock) { toast.success(`This election unlocked at ${plan} tier`); setOpen(false); onDone(); }
      else if (data.authorization_url) window.location.href = data.authorization_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="buy-election-btn" className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-300 rounded-md font-semibold text-sm hover:border-black transition-colors">
          <Ticket size={16} weight="fill" /> {paidTier ? `Election: ${paidTier}` : "Buy this election"}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Unlock this election</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-500 -mt-2">Pay once for a single election — no monthly subscription. Raises this election's voter limit.</p>
        <div className="space-y-3 mt-2">
          {["starter", "pro"].map((plan) => (
            <div key={plan} data-testid={`buy-tier-${plan}`} className="flex items-center justify-between border border-zinc-200 rounded-lg p-4">
              <div>
                <div className="font-semibold capitalize">{plan}</div>
                <div className="text-xs text-zinc-500">{plan === "starter" ? "Up to 1,000 voters" : "1,000+ voters, custom branding"}</div>
              </div>
              <button onClick={() => buy(plan)} disabled={busy === plan} className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md text-sm font-semibold hover:bg-zinc-800 transition-colors active:scale-95">
                {busy === plan && <SpinnerGap size={15} className="animate-spin" />}
                ${billing?.pricing?.[plan]?.per_election}
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ElectionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/elections/${id}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Layout back="/app"><p className="text-zinc-400 font-mono text-sm">loading…</p></Layout>;

  const el = data.election;
  const isLive = el.status === "live";

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/elections/${id}/publish`);
      toast.success("Election published & invitations sent");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const closeNow = async () => {
    setBusy(true);
    try {
      await api.post(`/elections/${id}/close`);
      toast.success("Election closed");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyResults = async () => {
    const link = `${window.location.origin}/results/${id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Public results link copied");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = link; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("Public results link copied");
      } catch {
        toast.error("Copy not supported here — link: " + link);
      }
    }
  };

  return (
    <Layout back="/app">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <StatusChip status={el.status} />
            {isLive && <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-mono"><LockSimple size={13} weight="fill" /> LIVE · EDITS AUDITED</span>}
            {el.status === "closed" && <span className="inline-flex items-center gap-1 text-xs text-zinc-500 font-mono"><LockSimple size={13} weight="fill" /> CLOSED · LOCKED</span>}
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter">{el.title}</h1>
          <p className="text-zinc-500 mt-1 max-w-2xl">{el.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {el.status === "closed" && (
            <Dialog>
              <DialogTrigger asChild>
                <button data-testid="declare-winners-btn" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#14B8A6] text-white rounded-md font-semibold text-sm hover:bg-[#0e9488] transition-colors active:scale-95">
                  <Trophy size={16} weight="fill" /> Declare winners
                </button>
              </DialogTrigger>
              <DialogContent className="bg-white sm:max-w-lg">
                <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Winners announcement</DialogTitle></DialogHeader>
                <p className="text-sm text-zinc-500 -mt-2 mb-1">Share the official results image, with winners highlighted, anywhere.</p>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-1">Tied positions are omitted from the announcement until you declare a winner in the <span className="font-semibold">Results</span> tab.</p>
                <div className="border border-zinc-200 rounded-lg overflow-hidden bg-zinc-50">
                  <img data-testid="winners-preview" src={`${API}/public/results/${id}/image`} alt="Winners" className="w-full" />
                </div>
                <div className="mt-4 flex justify-center">
                  <ShareResults imagePath={`/public/results/${id}/image`} shareUrl={`${window.location.origin}/results/${id}`} title={el.title} brand="#14B8A6" />
                </div>
              </DialogContent>
            </Dialog>
          )}
          {el.published && (
            <Link to={`/app/elections/${id}/monitor`} data-testid="live-monitor-btn" className="inline-flex items-center gap-2 px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm font-medium hover:border-[#1F6FE5] hover:text-[#1F6FE5] transition-colors">
              <Broadcast size={16} weight="fill" /> Live monitor
            </Link>
          )}
          {el.published && (
            <button data-testid="copy-results-link" onClick={copyResults} className="inline-flex items-center gap-2 px-3.5 py-2.5 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors">
              <Copy size={16} /> Results link
            </button>
          )}
          {el.status === "draft" && (
            <BuyElectionDialog electionId={id} paidTier={el.paid_tier} onDone={load} />
          )}
          {el.status === "draft" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="publish-btn" disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1F6FE5] text-white rounded-md font-semibold text-sm hover:bg-[#1552C9] transition-colors active:scale-95">
                  {busy ? <SpinnerGap size={16} className="animate-spin" /> : <RocketLaunch size={16} weight="fill" />} Publish
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Publish this election?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Voting tokens will be generated and invitations emailed to all voters. The structure (positions, candidates, dates) will lock once voting opens.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="confirm-publish" onClick={publish} className="bg-[#1F6FE5] hover:bg-[#1552C9]">Publish & send</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {isLive && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="close-btn" className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-md font-semibold text-sm hover:bg-red-700 transition-colors active:scale-95">Close now</button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Close voting now?</AlertDialogTitle>
                  <AlertDialogDescription>This immediately ends voting. Voters with unused links will no longer be able to vote.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="confirm-close" onClick={closeNow} className="bg-red-600 hover:bg-red-700">Close election</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-transparent border-b border-zinc-200 rounded-none w-full justify-start h-auto p-0 gap-6">
          {["overview", ...(el.nomination_for ? ["nominations"] : ["positions", "voters"]), "results", "audit"].map((t) => (
            <TabsTrigger
              key={t}
              value={t}
              data-testid={`tab-${t}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1F6FE5] data-[state=active]:text-[#1F6FE5] data-[state=active]:shadow-none text-zinc-500 px-0 pb-3 capitalize font-medium bg-transparent"
            >
              {t === "positions" ? "Positions & Candidates" : t === "audit" ? "Audit Log" : t === "nominations" ? "Nominations" : t}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="pt-8">
          <TabsContent value="overview"><OverviewTab data={data} onChange={load} /></TabsContent>
          {!el.nomination_for && <TabsContent value="positions"><PositionsTab electionId={id} positions={data.positions} status={el.status} onChange={load} /></TabsContent>}
          {!el.nomination_for && <TabsContent value="voters"><VotersTab electionId={id} election={el} onChange={load} /></TabsContent>}
          {el.nomination_for && <TabsContent value="nominations"><NominationsTab electionId={id} election={el} onChange={load} /></TabsContent>}
          <TabsContent value="results"><ResultsTab electionId={id} /></TabsContent>
          <TabsContent value="audit"><AuditTab electionId={id} /></TabsContent>
        </div>
      </Tabs>
    </Layout>
  );
}
